import React, { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { getAddressExplorerUrl } from "../../helper/x402/chains";

/**
 * Creator identity row in the sidebar.
 *
 * Renders three pieces of info:
 *   - @username (when HNS-registered) — big, primary identity.
 *   - Shortened 0x address — secondary, click-to-copy.
 *   - "Verify on explorer" link — opens the address on the chain's
 *     explorer so the payer can inspect the on-chain history of the
 *     receiving wallet before paying.
 *
 * When no HNS username is set (legacy wallet), falls back to the
 * short 0x address as the primary identity so "Pay <nobody>" never
 * happens.
 */
export function CreatorBadge({ link }) {
  const [copied, setCopied] = useState(false);
  // Tracked separately from `copied` so the badge can show a
  // "Copy failed — select manually" hint distinctly from a success
  // state. iOS Safari in private mode, some embedded WebViews, and
  // browsers where the clipboard permission prompt gets dismissed
  // all throw on `writeText` — we want to tell the user instead of
  // silently pretending it worked.
  const [copyError, setCopyError] = useState(false);

  const copyAddress = useCallback(async () => {
    const addr = link?.payTo;
    if (!addr) return;
    if (!navigator?.clipboard?.writeText) {
      // No Clipboard API at all (ancient browser, non-secure
      // context). Surface via toast + badge so the user has a
      // path forward (long-press to select).
      setCopyError(true);
      toast.error("Copy unavailable — tap and hold to select manually.");
      setTimeout(() => setCopyError(false), 2500);
      return;
    }
    try {
      // `writeText` returns a Promise. The previous non-awaited
      // call looked synchronous + always-succeeded, so a rejection
      // (permission denied) would render the success UI over a
      // broken clipboard. Awaiting inside try/catch fixes that.
      await navigator.clipboard.writeText(addr);
      setCopied(true);
      toast.success("Address copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopyError(true);
      toast.error("Couldn't copy — tap and hold to select manually.");
      setTimeout(() => setCopyError(false), 2500);
    }
  }, [link]);

  if (!link) return null;

  const primaryId = link.creator?.username
    ? `@${link.creator.username}`
    : link.creator?.displayAddress || "";
  const showAddressRow = Boolean(link.creator?.username); // already showing address as the primary when there's no username

  const explorerUrl = getAddressExplorerUrl(link.chainId, link.payTo);

  return (
    <div className="pay-creator">
      <div className="pay-creator__username">{primaryId}</div>
      {showAddressRow && (
        <div className="pay-creator__row">
          <span
            className={
              "pay-creator__address" +
              (copyError ? " pay-creator__address--error" : "")
            }
            title={copyError ? "Copy failed — select manually." : "Click to copy"}
            onClick={copyAddress}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              // Keyboard activation for a11y — Enter + Space both
              // trigger the copy, matching the behaviour users
              // expect from a button-semantic element.
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                copyAddress();
              }
            }}
          >
            {link.creator?.displayAddress}
            {copied ? " ✓" : copyError ? " ⚠" : ""}
          </span>
          {explorerUrl && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pay-creator__verify"
            >
              Verify
            </a>
          )}
        </div>
      )}
      {!showAddressRow && explorerUrl && (
        <div className="pay-creator__row">
          <a
            href={explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pay-creator__verify"
          >
            Verify on explorer
          </a>
        </div>
      )}
    </div>
  );
}
